import { useState, useCallback, useRef, useEffect } from 'react';
import { getApiBase } from '../utils/getApiBase';
import {
    formatLocalDateLabel,
    netNotificationsByPersonDate,
    parseLocalIsoDate,
    resolveEmployeeRecipient,
} from '../utils/notificationRecipient';

const DAY_MS = 24 * 60 * 60 * 1000;
const QUEUE_STORAGE_KEY = 'planner:notificationQueue';
const SENT_STORAGE_KEY = 'planner:notificationLastSent';

const TYPE_TITLES = {
    add_staff: 'Zostałeś dodany do wydarzenia',
    remove_staff: 'Zostałeś usunięty z wydarzenia',
    propose_staff: 'Propozycja udziału w wydarzeniu',
};

const readStorage = (storage, key) => {
    if (!storage) return null;
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

const loadJson = (key, fallback) => {
    try {
        const localRaw = readStorage(typeof localStorage !== 'undefined' ? localStorage : null, key);
        if (localRaw) {
            const parsed = JSON.parse(localRaw);
            return parsed == null ? fallback : parsed;
        }
        const sessionRaw = readStorage(typeof sessionStorage !== 'undefined' ? sessionStorage : null, key);
        if (sessionRaw) {
            const parsed = JSON.parse(sessionRaw);
            try {
                if (typeof localStorage !== 'undefined') localStorage.setItem(key, sessionRaw);
            } catch { /* quota / private mode */ }
            return parsed == null ? fallback : parsed;
        }
    } catch {
        return fallback;
    }
    return fallback;
};

const saveJson = (key, value) => {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(key, JSON.stringify(value));
    } catch { /* quota / private mode */ }
};

const loadQueue = () => {
    const stored = loadJson(QUEUE_STORAGE_KEY, []);
    return Array.isArray(stored) ? stored : [];
};

const loadLastSent = () => {
    const stored = loadJson(SENT_STORAGE_KEY, []);
    return new Map(Array.isArray(stored) ? stored : []);
};

const groupConsecutiveDates = (dates = []) => {
    const sorted = [...new Set(dates.filter(Boolean))].sort();
    if (sorted.length === 0) return [];

    const groups = [];
    let current = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
        const prev = parseLocalIsoDate(sorted[i - 1]);
        const curr = parseLocalIsoDate(sorted[i]);
        if (!prev || !curr) {
            groups.push(current);
            current = [sorted[i]];
            continue;
        }
        const diffDays = Math.round((curr.getTime() - prev.getTime()) / DAY_MS);
        if (diffDays === 1) {
            current.push(sorted[i]);
        } else {
            groups.push(current);
            current = [sorted[i]];
        }
    }
    groups.push(current);
    return groups;
};

const formatShortDate = (iso, includeYear = false) => {
    const date = parseLocalIsoDate(iso);
    if (!date) return iso;
    const day = date.getDate();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    if (includeYear) {
        return `${day}.${month}.${date.getFullYear()}`;
    }
    return `${day}.${month}`;
};

const formatRangeLabel = (dates) => {
    if (!dates || dates.length === 0) return '';
    if (dates.length === 1) return formatShortDate(dates[0], true);

    const start = parseLocalIsoDate(dates[0]);
    const end = parseLocalIsoDate(dates[dates.length - 1]);
    if (!start || !end) {
        return `${dates[0]} - ${dates[dates.length - 1]}`;
    }

    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();

    if (sameMonth) {
        const month = String(end.getMonth() + 1).padStart(2, '0');
        return `${start.getDate()}-${end.getDate()}.${month}`;
    }

    if (sameYear) {
        return `${formatShortDate(dates[0])} - ${formatShortDate(dates[dates.length - 1])}.${end.getFullYear()}`;
    }

    return `${formatShortDate(dates[0], true)} - ${formatShortDate(dates[dates.length - 1], true)}`;
};

const buildNotificationBody = (eventName, dates) => {
    if (!dates || dates.length === 0) {
        return eventName || 'Nowe wydarzenie';
    }
    if (dates.length === 1) {
        return `${eventName} (${formatLocalDateLabel(dates[0])})`;
    }
    return `${eventName} w dniach ${formatRangeLabel(dates)}`;
};

/**
 * Hook do zarządzania kolejką powiadomień
 * Agreguje zmiany w przypisaniach do wydarzeń i pozwala na ich wysłanie lub edycję
 */
export function useNotificationQueue() {
    const [queue, setQueue] = useState(loadQueue);
    const [isQueueVisible, setIsQueueVisible] = useState(() => loadQueue().length > 0);
    const [isSending, setIsSending] = useState(false);
    const queueIdRef = useRef(0);
    const lastSentStateRef = useRef(loadLastSent());
    const sendingRef = useRef(false);

    useEffect(() => {
        saveJson(QUEUE_STORAGE_KEY, queue);
    }, [queue]);

    useEffect(() => {
        saveJson(SENT_STORAGE_KEY, Array.from(lastSentStateRef.current.entries()));
    }, [queue]);

    const persistLastSent = useCallback(() => {
        saveJson(SENT_STORAGE_KEY, Array.from(lastSentStateRef.current.entries()));
    }, []);

    const addToQueue = useCallback((notification) => {
        const id = `notif-${Date.now()}-${++queueIdRef.current}`;
        const newNotification = {
            id,
            ...notification,
            timestamp: new Date().toISOString(),
            selected: notification.selected !== false,
        };

        setQueue((prev) => {
            const existing = prev.find((n) =>
                n.type === notification.type &&
                n.eventName === notification.eventName &&
                n.date === notification.date &&
                n.staffName === notification.staffName
            );

            if (existing) {
                return prev.map((n) => (n.id === existing.id ? { ...newNotification, id: existing.id } : n));
            }

            return [...prev, newNotification];
        });

        setIsQueueVisible(true);
    }, []);

    const removeFromQueue = useCallback((id) => {
        setQueue((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const removeFromQueueByKey = useCallback((eventName, date, staffName) => {
        setQueue((prev) => prev.filter((n) =>
            !(n.eventName === eventName && n.date === date && n.staffName === staffName)
        ));
    }, []);

    const toggleNotification = useCallback((id) => {
        setQueue((prev) => prev.map((n) =>
            n.id === id ? { ...n, selected: !n.selected } : n
        ));
    }, []);

    const clearQueue = useCallback(() => {
        setQueue([]);
        setIsQueueVisible(false);
    }, []);

    const sendSelectedNotifications = useCallback(async (employees = []) => {
        if (sendingRef.current) {
            return { success: false, message: 'Wysyłanie już trwa' };
        }

        const selected = queue.filter((n) => n.selected);
        if (selected.length === 0) {
            return { success: false, message: 'Brak zaznaczonych powiadomień' };
        }

        sendingRef.current = true;
        setIsSending(true);

        try {
            const resolved = selected.map((notif) => {
                const recipient = resolveEmployeeRecipient(notif.staffName, notif.staffId || notif.userId, employees);
                return { notif, recipient };
            });

            const unmapped = resolved.filter((item) => !item.recipient.mapped);
            const mapped = resolved.filter((item) => item.recipient.mapped);

            if (unmapped.length > 0) {
                setQueue((prev) => prev.map((n) => {
                    const hit = unmapped.find((item) => item.notif.id === n.id);
                    if (!hit) return n;
                    return {
                        ...n,
                        mappingError: `Nie znaleziono konta dla „${hit.notif.staffName}”`,
                    };
                }));
            }

            if (mapped.length === 0) {
                return {
                    success: false,
                    message: 'Nie można wysłać — brak zmapowanych odbiorców',
                    unmapped: unmapped.length,
                };
            }

            const netted = netNotificationsByPersonDate(mapped.map(({ notif, recipient }) => ({
                ...notif,
                userId: recipient.userId,
                staffId: recipient.staffId,
                lookupKeys: recipient.lookupKeys,
            })));

            const notificationsByUser = {};
            netted.forEach((notif) => {
                const userId = notif.userId;
                if (!notificationsByUser[userId]) notificationsByUser[userId] = [];
                notificationsByUser[userId].push(notif);
            });

            const notificationsToSend = [];
            Object.entries(notificationsByUser).forEach(([userId, userNotifications]) => {
                const groupedByEvent = {};
                userNotifications.forEach((notif) => {
                    const eventName = notif.eventName || 'Nieznane wydarzenie';
                    const type = notif.type || 'add_staff';
                    if (!groupedByEvent[eventName]) groupedByEvent[eventName] = {};
                    if (!groupedByEvent[eventName][type]) groupedByEvent[eventName][type] = [];
                    groupedByEvent[eventName][type].push(notif);
                });

                Object.entries(groupedByEvent).forEach(([eventName, types]) => {
                    Object.entries(types).forEach(([type, typeNotifications]) => {
                        const allDates = [...new Set(typeNotifications.map((n) => n.date).filter(Boolean))].sort();
                        const groupedRanges = groupConsecutiveDates(allDates);
                        const ranges = groupedRanges.length > 0 ? groupedRanges : [allDates];
                        const title = TYPE_TITLES[type] || 'Nowe powiadomienie';
                        const lookupKeys = typeNotifications[0]?.lookupKeys || [];

                        ranges.forEach((rangeDates, rangeIndex) => {
                            const relatedNotifications = typeNotifications.filter((n) => rangeDates.includes(n.date));
                            const referenceNotif = relatedNotifications[0] || typeNotifications[0] || {};
                            const firstDate = rangeDates[0] || referenceNotif.date || null;
                            const sourceIds = relatedNotifications.map((n) => n.id);

                            notificationsToSend.push({
                                userId,
                                lookupKeys,
                                sourceIds,
                                notification: {
                                    title,
                                    body: buildNotificationBody(eventName, rangeDates),
                                    icon: '/icons/Logo-192.png',
                                    badge: '/icons/Logo-badge.png',
                                    tag: `notification-${eventName}-${type}-${rangeIndex}`,
                                    data: {
                                        type,
                                        eventName,
                                        dates: rangeDates,
                                        date: firstDate,
                                        eventId: referenceNotif.eventId,
                                        calendarId: referenceNotif.calendarId,
                                        staffName: referenceNotif.staffName,
                                        requestedBy: referenceNotif.requestedBy,
                                        requestedByName: referenceNotif.requestedByName || referenceNotif.requestedBy,
                                        lookupKeys,
                                        allNotifications: relatedNotifications,
                                    },
                                    actions: type === 'propose_staff'
                                        ? [
                                            { action: 'accept', title: 'Akceptuj' },
                                            { action: 'reject', title: 'Odrzuć' },
                                        ]
                                        : [],
                                },
                            });
                        });
                    });
                });
            });

            if (notificationsToSend.length === 0) {
                return { success: false, message: 'Brak powiadomień do wysłania' };
            }

            const sendPromises = notificationsToSend.map(async ({ userId, lookupKeys, sourceIds, notification }) => {
                try {
                    const response = await fetch(`${getApiBase()}/api/sendPushNotification`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            userIds: [userId],
                            lookupKeys,
                            notification,
                        }),
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        throw new Error(`HTTP ${response.status}: ${errorText}`);
                    }

                    const result = await response.json();
                    if (!result.success) {
                        console.warn(`[NotificationQueue] Błąd wysyłania do ${userId}:`, result.error || 'Nieznany błąd');
                    }
                    return { success: result.success !== false, userId, sourceIds, result };
                } catch (error) {
                    console.error(`[NotificationQueue] Błąd wysyłania do ${userId}:`, error);
                    return { success: false, userId, sourceIds, error: error.message };
                }
            });

            const results = await Promise.all(sendPromises);
            const successful = results.filter((r) => r.success && r.result?.sent > 0).length;
            const noSubscription = results.filter((r) => r.success && r.result?.sent === 0).length;
            const failed = results.filter((r) => !r.success);

            const deliveredIds = new Set();
            results.forEach((r) => {
                if (r.success) {
                    (r.sourceIds || []).forEach((id) => deliveredIds.add(id));
                }
            });

            netted.forEach((notif) => {
                if (!deliveredIds.has(notif.id)) return;
                const key = `${notif.eventName}::${notif.date}::${notif.staffName}`;
                lastSentStateRef.current.set(key, {
                    type: notif.type,
                    sent: true,
                    timestamp: Date.now(),
                });
            });
            persistLastSent();

            const deliveredProposals = netted.filter(
                (n) => n.type === 'propose_staff' && deliveredIds.has(n.id)
            );
            if (deliveredProposals.length > 0) {
                try {
                    const emailResponse = await fetch(`${getApiBase()}/api/sendBatchEmails`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            pendingProposals: deliveredProposals.map((n) => ({
                                staffName: n.staffName,
                                eventName: n.eventName,
                                date: n.date,
                                eventId: n.eventId,
                                calendarId: n.calendarId,
                            })),
                        }),
                    });
                    if (!emailResponse.ok) {
                        const emailText = await emailResponse.text();
                        console.warn('[NotificationQueue] sendBatchEmails HTTP', emailResponse.status, emailText);
                    }
                } catch (emailError) {
                    console.warn('[NotificationQueue] sendBatchEmails failed:', emailError);
                }
            }

            setQueue((prev) => {
                const next = prev.filter((n) => !deliveredIds.has(n.id));
                if (next.length === 0) setIsQueueVisible(false);
                return next;
            });

            if (typeof BroadcastChannel !== 'undefined') {
                const channel = new BroadcastChannel('planner-updates');
                channel.postMessage({
                    type: 'data-updated',
                    timestamp: Date.now(),
                    source: 'notifications-sent',
                });
                channel.close();
            }

            let message = 'Wysłano pomyślnie';
            if (failed.length > 0 && successful === 0 && noSubscription === 0) {
                message = 'Nie udało się wysłać powiadomień';
            } else if (failed.length > 0) {
                message = 'Wysłano z błędami — nieudane zostają w kolejce';
            } else if (noSubscription > 0 && successful === 0) {
                message = 'Zapisano w aplikacji. Odbiorcy nie mają włączonego push na telefonie.';
            } else if (noSubscription > 0) {
                message = `Push: ${successful}, tylko w aplikacji: ${noSubscription}`;
            }
            if (unmapped.length > 0) {
                message += ` ${unmapped.length} osób bez konta zostało w kolejce.`;
            }

            return {
                success: failed.length === 0 || successful > 0 || noSubscription > 0,
                sent: successful,
                noSubscription,
                failed: failed.length,
                unmapped: unmapped.length,
                total: notificationsToSend.length,
                message,
            };
        } catch (error) {
            console.error('[NotificationQueue] Błąd wysyłania powiadomień:', error);
            return { success: false, message: error.message };
        } finally {
            sendingRef.current = false;
            setIsSending(false);
        }
    }, [queue, persistLastSent]);

    const rejectAll = useCallback(() => {
        clearQueue();
    }, [clearQueue]);

    const getLastSentState = useCallback((eventName, date, staffName) => {
        const key = `${eventName}::${date}::${staffName}`;
        return lastSentStateRef.current.get(key) || null;
    }, []);

    const clearLastSentState = useCallback((eventName, date, staffName) => {
        const key = `${eventName}::${date}::${staffName}`;
        lastSentStateRef.current.delete(key);
        persistLastSent();
    }, [persistLastSent]);

    return {
        queue,
        isQueueVisible,
        setIsQueueVisible,
        isSending,
        addToQueue,
        removeFromQueue,
        removeFromQueueByKey,
        toggleNotification,
        clearQueue,
        sendSelectedNotifications,
        rejectAll,
        getLastSentState,
        clearLastSentState,
        queueCount: queue.length,
        selectedCount: queue.filter((n) => n.selected).length,
        unmappedCount: queue.filter((n) => n.mappingError).length,
    };
}
